import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send, User, AtSign } from 'lucide-react';

type Mensagem = {
  id: string;
  mensagem: string;
  created_at: string;
  id_usuario: string;
  profiles?: {
    user_id: string;
    full_name: string | null;
    email: string | null;
  };
  mencoes?: Array<{
    id_usuario: string;
    profiles?: {
      user_id: string;
      full_name: string | null;
      email: string | null;
    };
  }>;
};

type Usuario = {
  user_id: string;
  full_name: string | null;
  email: string | null;
};

interface MensagensTabProps {
  tela: string;
  idRegistro: string;
}

export default function MensagensTab({ tela, idRegistro }: MensagensTabProps) {
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<Usuario[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadMensagens();
    loadUsuarios();
  }, [tela, idRegistro]);

  const loadUsuarios = async () => {
    try {
      // Buscar todos os usuários do sistema
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, email')
        .not('full_name', 'is', null);

      if (error) throw error;
      setUsuarios(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadMensagens = async () => {
    try {
      const { data, error } = await supabase
        .from('mensagens')
        .select('*')
        .eq('tela', tela)
        .eq('id_registro', idRegistro)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      if (data && data.length > 0) {
        // Buscar informações dos usuários das mensagens
        const userIds = [...new Set(data.map(msg => msg.id_usuario))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', userIds);

        // Buscar menções para todas as mensagens
        const { data: mencoes } = await supabase
          .from('mensagens_mencoes')
          .select(`
            id_mensagem,
            id_usuario
          `)
          .in('id_mensagem', data.map(msg => msg.id));

        // Buscar perfis dos usuários mencionados
        const mentionedUserIds = [...new Set(mencoes?.map(m => m.id_usuario) || [])];
        const { data: mentionedProfiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, email')
          .in('user_id', mentionedUserIds);

        const mensagensComPerfis = data.map(msg => ({
          ...msg,
          profiles: profiles?.find(p => p.user_id === msg.id_usuario),
          mencoes: mencoes?.filter(m => m.id_mensagem === msg.id).map(m => ({
            id_usuario: m.id_usuario,
            profiles: mentionedProfiles?.find(p => p.user_id === m.id_usuario)
          }))
        }));
        
        setMensagens(mensagensComPerfis);
      } else {
        setMensagens([]);
      }
    } catch (error: any) {
      console.error('Erro ao carregar mensagens:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar mensagens: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setNovaMensagem(value);
    setCursorPosition(cursorPos);

    // Verificar se foi digitado @
    if (value[cursorPos - 1] === '@') {
      setShowMentionDropdown(true);
      setMentionSearchTerm('');
    } else if (showMentionDropdown) {
      // Capturar texto após o @ para busca
      const lastAtIndex = value.lastIndexOf('@', cursorPos);
      if (lastAtIndex !== -1) {
        const searchTerm = value.substring(lastAtIndex + 1, cursorPos);
        setMentionSearchTerm(searchTerm);
      } else {
        setShowMentionDropdown(false);
      }
    }
  };

  const selectMention = (usuario: Usuario) => {
    const lastAtIndex = novaMensagem.lastIndexOf('@', cursorPosition);
    const beforeAt = novaMensagem.substring(0, lastAtIndex);
    const afterCursor = novaMensagem.substring(cursorPosition);
    
    const newText = `${beforeAt}@${usuario.full_name || usuario.email} ${afterCursor}`;
    setNovaMensagem(newText);
    setShowMentionDropdown(false);
    
    // Adicionar à lista de menções selecionadas
    if (!selectedMentions.find(m => m.user_id === usuario.user_id)) {
      setSelectedMentions([...selectedMentions, usuario]);
    }

    // Focar de volta no textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
  };

  const filteredUsuarios = usuarios.filter(user =>
    (user.full_name?.toLowerCase().includes(mentionSearchTerm.toLowerCase()) ||
     user.email.toLowerCase().includes(mentionSearchTerm.toLowerCase()))
  );

  const enviarMensagem = async () => {
    if (!novaMensagem.trim()) return;

    setEnviando(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Inserir mensagem
      const { data: mensagemData, error } = await supabase
        .from('mensagens')
        .insert({
          tela,
          id_registro: idRegistro,
          id_usuario: user.id,
          mensagem: novaMensagem.trim()
        })
        .select()
        .single();

      if (error) throw error;

      // Inserir menções se houver
      if (selectedMentions.length > 0) {
        const mencoesData = selectedMentions.map(mention => ({
          id_mensagem: mensagemData.id,
          id_usuario: mention.user_id
        }));

        const { error: mencoesError } = await supabase
          .from('mensagens_mencoes')
          .insert(mencoesData);

        if (mencoesError) {
          console.error('Erro ao salvar menções:', mencoesError);
        }
      }

      setNovaMensagem('');
      setSelectedMentions([]);
      loadMensagens();
      
      toast({
        title: "Sucesso",
        description: "Mensagem enviada com sucesso!",
      });
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      toast({
        title: "Erro",
        description: "Erro ao enviar mensagem: " + error.message,
        variant: "destructive",
      });
    } finally {
      setEnviando(false);
    }
  };

  const renderMessageWithMentions = (mensagem: string, mencoes?: Array<{ id_usuario: string; profiles?: { full_name: string | null; email: string | null } }>) => {
    if (!mencoes || mencoes.length === 0) {
      return mensagem;
    }

    let processedMessage = mensagem;
    mencoes.forEach(mention => {
      const mentionName = mention.profiles?.full_name || mention.profiles?.email || 'Usuário';
      const mentionRegex = new RegExp(`@${mentionName}`, 'g');
      processedMessage = processedMessage.replace(
        mentionRegex,
        `<span class="mention-highlight">@${mentionName}</span>`
      );
    });

    return <div dangerouslySetInnerHTML={{ __html: processedMessage }} />;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !showMentionDropdown) {
      e.preventDefault();
      enviarMensagem();
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Campo para nova mensagem */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Nova Mensagem</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              placeholder="Digite sua mensagem... Use @ para mencionar usuários"
              value={novaMensagem}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="min-h-[100px] pr-10"
            />
            <div className="absolute top-2 right-2 text-muted-foreground">
              <AtSign className="h-4 w-4" />
            </div>
            
            {/* Dropdown de menções */}
            {showMentionDropdown && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredUsuarios.length > 0 ? (
                  filteredUsuarios.map((usuario) => (
                    <div
                      key={usuario.user_id}
                      className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer"
                      onClick={() => selectMention(usuario)}
                    >
                      <User className="h-4 w-4 text-primary" />
                      <div>
                        <div className="font-medium text-sm">
                          {usuario.full_name || 'Sem nome'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {usuario.email}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground">
                    Nenhum usuário encontrado
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mostrar menções selecionadas */}
          {selectedMentions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Mencionando:</span>
              {selectedMentions.map((mention) => (
                <span
                  key={mention.user_id}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                >
                  <AtSign className="h-3 w-3" />
                  {mention.full_name || mention.email}
                </span>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button 
              onClick={enviarMensagem} 
              disabled={!novaMensagem.trim() || enviando}
              className="gap-2"
            >
              <Send className="h-4 w-4" />
              {enviando ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de mensagens */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico de Mensagens</CardTitle>
        </CardHeader>
        <CardContent>
          {mensagens.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Nenhuma mensagem encontrada.
            </p>
          ) : (
            <ScrollArea className="h-[400px] w-full">
              <div className="space-y-4">
                {mensagens.map((mensagem) => (
                  <div key={mensagem.id} className="border-l-4 border-primary/20 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">
                          {mensagem.profiles?.full_name || mensagem.profiles?.email || 'Usuário'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(mensagem.created_at).toLocaleString('pt-BR')}
                        </div>
                      </div>
                    </div>
                    <div className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-md">
                      {renderMessageWithMentions(mensagem.mensagem, mensagem.mencoes)}
                    </div>
                    {mensagem.mencoes && mensagem.mencoes.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {mensagem.mencoes.map((mencao, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                          >
                            <AtSign className="h-3 w-3" />
                            {mencao.profiles?.full_name || mencao.profiles?.email || 'Usuário'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      <style dangerouslySetInnerHTML={{
        __html: `
          .mention-highlight {
            background-color: hsl(var(--primary) / 0.1);
            color: hsl(var(--primary));
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-weight: 500;
          }
        `
      }} />
    </div>
  );
}